import { Heading } from '@assembly-js/design-system';
import { generateHTML, generateJSON } from '@tiptap/core';
import { useMemo } from 'react';

import { RichTextStyles } from 'src/components/tiptap/RichTextStyles';
import { noteEditorExtensions } from 'src/features/notes/NoteModal/NoteEditor';
import { Note } from 'src/services/api/notesApi';
import { DateUtils } from 'src/utils';

export const NotesListItem = ({
  note,
  onClick,
}: {
  note: Note;
  onClick: () => void;
}) => {
  const isSaving = note.id.startsWith('temp-');
  /**
   * The platform API allows users to save arbitrary data as the content of a note.
   * This code normalizes the note content to guarantee that
   * 1. The preview rendered here and the view in the editor are consistent
   * 2. The preview rendered here cannot render arbitrary HTML and is locked down
   * to what the note editor allows
   */
  const content = useMemo(() => {
    const jsonContent = generateJSON(note.content, noteEditorExtensions);
    return generateHTML(jsonContent, noteEditorExtensions);
  }, [note.content]);

  return (
    <button
      className="appearance-none bg-transparent p-0 w-full text-left hover:cursor-pointer px-4 hover:bg-secondary-hover border-non-hover-border border-solid border-l-0 border-r-0 border-t-0 border-b disabled:opacity-50 disabled:cursor-default"
      onClick={onClick}
      disabled={isSaving}
      aria-label={
        isSaving ? `Note "${note.title}" is saving, please wait` : undefined
      }
    >
      <div className="w-full h-full py-3">
        <Heading
          size="sm"
          className="line-clamp-1 text-black-heading break-all mb-1"
        >
          {note.title} {`(${DateUtils.formatDate(note.createdAt)})`}
        </Heading>
        {/* this check prevents the note content from rendering and taking up space in the preview if it is empty */}
        {content !== '<p></p>' && (
          <RichTextStyles>
            <div
              dangerouslySetInnerHTML={{ __html: content }}
              className="w-full line-clamp-2 text-secondary break-words"
            />
          </RichTextStyles>
        )}
      </div>
    </button>
  );
};
